import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, Link2, Link2Off, CheckCircle2 } from 'lucide-react';
import GoogleIcon from '@/components/GoogleIcon';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';

/**
 * Connect this rep's own Google account.
 *
 * This replaces the card that used to say "Mike's Calendar" to everybody and
 * whose button called a browser side service role API that no longer exists.
 * Base44 sent every email through one shared connection with a hardcoded
 * connection id, so mail from any rep arrived as Mike. Each rep authorises
 * their own account now, and the follow up goes out as whoever captured the
 * contact.
 */
export default function GoogleConnection() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [working, setWorking] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['google-status'],
    queryFn: () => base44.google.status(),
  });

  // The callback sends the browser back with ?google=connected or =failed.
  useEffect(() => {
    const result = searchParams.get('google');
    if (!result) return;
    if (result === 'connected') toast.success('Google connected');
    if (result === 'failed') toast.error('Google could not be connected');
    queryClient.invalidateQueries({ queryKey: ['google-status'] });
    searchParams.delete('google');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const connect = async () => {
    setWorking(true);
    try {
      const { url } = await base44.google.start();
      // A full navigation, not a popup. Consent on a phone in a popup is a
      // reliable way to lose the window behind the browser chrome.
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || 'Could not start the Google connection');
      setWorking(false);
    }
  };

  const disconnect = async () => {
    setWorking(true);
    try {
      const result = await base44.google.disconnect();
      toast.success(result.message || 'Disconnected');
      queryClient.invalidateQueries({ queryKey: ['google-status'] });
    } catch (err) {
      toast.error(err.message || 'Could not disconnect');
    } finally {
      setWorking(false);
    }
  };

  if (isLoading) return null;

  // Nothing a rep can do about a missing client id, so do not show them a
  // button that cannot work.
  if (status && !status.configured) return null;

  const connected = status?.connected;

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl">
      <GoogleIcon className="w-4 h-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium">Google</p>
        {connected ? (
          <p className="text-xs text-foreground truncate flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
            Sending as {status.google_email || 'your account'}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Connect to send follow ups from your own address
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={connected ? disconnect : connect}
        disabled={working}
        className="min-h-8 rounded-lg text-xs font-medium shrink-0"
      >
        {working ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : connected ? (
          <><Link2Off className="w-3.5 h-3.5 mr-1" />Disconnect</>
        ) : (
          <><Link2 className="w-3.5 h-3.5 mr-1" />Connect</>
        )}
      </Button>
    </div>
  );
}
